/**
 * Google Analytics utility functions
 * Provides helper functions for tracking user interactions
 */

/**
 * Checks if Google Analytics is available
 * @returns {boolean} True if gtag is available
 */
function isAnalyticsAvailable() {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/**
 * Tracks an event in Google Analytics
 * @param {string} eventName - Event name
 * @param {Object} eventParams - Event parameters
 */
export function trackEvent(eventName, eventParams = {}) {
    if (!isAnalyticsAvailable()) {
        return;
    }

    try {
        window.gtag('event', eventName, eventParams);
    } catch (error) {
        console.error('Error tracking event:', error);
    }
}

/**
 * Tracks when a package is added to cart
 * @param {Object} packageData - Package data
 * @param {string} packageData.packageCode - Package code
 * @param {string} packageData.name - Package name
 * @param {number} packageData.price - Package price
 * @param {string} [packageData.esimTranNo] - eSIM transaction number (for topup)
 */
export function trackAddToCart(packageData) {
    const isTopup = !!packageData.esimTranNo;
    
    trackEvent('add_to_cart', {
        currency: 'USD',
        value: parseFloat(packageData.price) || 0,
        items: [{
            item_id: packageData.packageCode,
            item_name: packageData.name || 'Unknown Package',
            price: parseFloat(packageData.price) || 0,
            quantity: 1,
            item_category: isTopup ? 'Topup' : 'eSIM Plan'
        }],
        event_category: 'ecommerce',
        event_label: isTopup ? 'topup' : 'plan'
    });
}

/**
 * Tracks when checkout is initiated
 * @param {Array} cartItems - Cart items
 */
export function trackBeginCheckout(cartItems) {
    const totalValue = cartItems.reduce((sum, item) => {
        return sum + (parseFloat(item.price) * item.quantity);
    }, 0);

    trackEvent('begin_checkout', {
        currency: 'USD',
        value: totalValue,
        items: cartItems.map(item => ({
            item_id: item.packageCode,
            item_name: item.name || 'Unknown Package',
            price: parseFloat(item.price) || 0,
            quantity: item.quantity,
            item_category: item.esimTranNo ? 'Topup' : 'eSIM Plan'
        })),
        event_category: 'ecommerce'
    });
}

/**
 * Tracks when a purchase is completed
 * @param {string} transactionId - Transaction ID (session ID)
 * @param {Array} items - Purchased items
 * @param {number} value - Total value
 */
export function trackPurchase(transactionId, items, value) {
    trackEvent('purchase', {
        transaction_id: transactionId,
        currency: 'USD',
        value: value,
        items: items.map(item => ({
            item_id: item.packageCode,
            item_name: item.name || 'Unknown Package',
            price: parseFloat(item.price) || 0,
            quantity: item.quantity,
            item_category: item.esimTranNo ? 'Topup' : 'eSIM Plan'
        })),
        event_category: 'ecommerce'
    });
}

/**
 * Tracks when checkout is canceled
 * @param {Array} cartItems - Cart items that were abandoned
 */
export function trackCheckoutCanceled(cartItems) {
    const totalValue = cartItems.reduce((sum, item) => {
        return sum + (parseFloat(item.price) * item.quantity);
    }, 0);

    trackEvent('checkout_canceled', {
        currency: 'USD',
        value: totalValue,
        items: cartItems.map(item => ({
            item_id: item.packageCode,
            item_name: item.name || 'Unknown Package',
            price: parseFloat(item.price) || 0,
            quantity: item.quantity
        })),
        event_category: 'ecommerce'
    });
}

/**
 * Tracks when cart is abandoned (closed without purchase)
 * @param {Array} cartItems - Cart items
 */
export function trackCartAbandoned(cartItems) {
    if (cartItems.length === 0) return;

    const totalValue = cartItems.reduce((sum, item) => {
        return sum + (parseFloat(item.price) * item.quantity);
    }, 0);

    trackEvent('cart_abandoned', {
        currency: 'USD',
        value: totalValue,
        items: cartItems.map(item => ({
            item_id: item.packageCode,
            item_name: item.name || 'Unknown Package',
            price: parseFloat(item.price) || 0,
            quantity: item.quantity
        })),
        event_category: 'ecommerce'
    });
}

/**
 * Tracks user login
 */
export function trackLogin() {
    trackEvent('login', {
        event_category: 'engagement',
        event_label: 'user_login'
    });
}

/**
 * Tracks user registration
 */
export function trackSignUp() {
    trackEvent('sign_up', {
        event_category: 'engagement',
        event_label: 'user_registration'
    });
}

/**
 * Tracks when user searches for plans
 * @param {string} searchQuery - Search query
 */
export function trackSearch(searchQuery) {
    trackEvent('search', {
        search_term: searchQuery,
        event_category: 'engagement',
        event_label: 'plan_search'
    });
}

/**
 * Tracks when user views package details
 * @param {string} packageCode - Package code
 * @param {string} packageName - Package name
 */
export function trackViewItem(packageCode, packageName) {
    trackEvent('view_item', {
        item_id: packageCode,
        item_name: packageName || 'Unknown Package',
        event_category: 'ecommerce',
        event_label: 'view_package_details'
    });
}

/**
 * Tracks when user suspends an eSIM
 * @param {string} esimTranNo - eSIM transaction number
 */
export function trackEsimSuspend(esimTranNo) {
    trackEvent('esim_suspend', {
        event_category: 'engagement',
        event_label: 'esim_management',
        esim_tran_no: esimTranNo
    });
}

/**
 * Tracks when user unsuspends an eSIM
 * @param {string} esimTranNo - eSIM transaction number
 */
export function trackEsimUnsuspend(esimTranNo) {
    trackEvent('esim_unsuspend', {
        event_category: 'engagement',
        event_label: 'esim_management',
        esim_tran_no: esimTranNo
    });
}

/**
 * Tracks when user clicks on topup button
 * @param {string} esimTranNo - eSIM transaction number
 */
export function trackTopupClick(esimTranNo) {
    trackEvent('topup_click', {
        event_category: 'engagement',
        event_label: 'topup_initiated',
        esim_tran_no: esimTranNo
    });
}

/**
 * Tracks when user changes language
 * @param {string} language - Language code (es, en)
 */
export function trackLanguageChange(language) {
    trackEvent('language_change', {
        event_category: 'engagement',
        event_label: 'language_selection',
        language: language
    });
}

/**
 * Tracks when user views FAQ section
 */
export function trackFAQView() {
    trackEvent('faq_view', {
        event_category: 'engagement',
        event_label: 'faq_section'
    });
}

/**
 * Tracks when user opens FAQ item
 * @param {number} faqNumber - FAQ item number
 */
export function trackFAQOpen(faqNumber) {
    trackEvent('faq_open', {
        event_category: 'engagement',
        event_label: 'faq_interaction',
        faq_number: faqNumber
    });
}

